import type { HandState, PlayerId, Seat } from '@/engine/types';
import type { Position, PreflopChart, Situation } from '../types';
import { chartFor } from './charts';
import { handClass } from './rank';

/**
 * Map a player's seat to a 6-max position name relative to the button.
 * Positions assume 6-handed; with fewer players the roles compress (BTN = SB at HU).
 */
export function positionOf(
  button: Seat,
  playerSeat: Seat,
  activeSeats: ReadonlyArray<Seat>,
): Position {
  if (activeSeats.length === 0) return 'BTN';
  if (activeSeats.length === 2) {
    return playerSeat === button ? 'BTN' : 'BB';
  }
  const ordered: Seat[] = [];
  for (let i = 0; i < 6; i++) {
    const s = ((button + i) % 6) as Seat;
    if (activeSeats.includes(s)) ordered.push(s);
  }
  // Order starts at button, clockwise: BTN, SB, BB, UTG, HJ, CO
  const seatToPos: Position[] = ['BTN', 'SB', 'BB', 'UTG', 'HJ', 'CO'];
  const idx = ordered.indexOf(playerSeat);
  return seatToPos[idx] ?? 'BTN';
}

/**
 * Infer the preflop situation from the hand's action history.
 * Returns 'open' when no raise yet, 'vs-open' after one raise, '3bet' after
 * two raises, etc.
 */
export function situationFor(state: HandState, playerId: PlayerId): Situation {
  let raises = 0;
  for (const a of state.actionHistory) {
    if (a.type === 'raise' || a.type === 'bet') raises += 1;
    if (a.type === 'allin') raises += 1;
  }
  if (raises === 0) return 'open';
  if (raises === 1) return 'vs-open';
  if (raises === 2) {
    // Did this player make the first raise? Then it's vs-3bet.
    const myFirstRaise = state.actionHistory.find(
      (a) => a.playerId === playerId && (a.type === 'raise' || a.type === 'bet'),
    );
    return myFirstRaise ? 'vs-3bet' : '3bet';
  }
  return '4bet';
}

export type PreflopFrequency = {
  chart: PreflopChart;
  classKey: string;
  aggressiveFreq: number;
  position: Position;
  situation: Situation;
};

/**
 * Look up the preflop frequency for the current (state, player).
 * Returns the full chart row plus the specific hand's aggressive-action
 * frequency in [0, 1].
 */
export function lookupPreflop(state: HandState, playerId: PlayerId): PreflopFrequency {
  const player = state.players.find((p) => p.id === playerId);
  if (!player?.holeCards) {
    throw new Error(`lookupPreflop: player ${playerId} has no hole cards`);
  }
  const activeSeats = state.players.filter((p) => !p.eliminated && p.stack > 0).map((p) => p.seat);
  const position = positionOf(state.button, player.seat, activeSeats);
  const situation = situationFor(state, playerId);
  const chart = chartFor({ position, situation });
  const classKey = handClass(player.holeCards);
  const aggressiveFreq = chart[classKey] ?? 0;
  return { chart, classKey, aggressiveFreq, position, situation };
}
